import { useEffect, useMemo, useRef, useState } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { api } from "@/lib/api";
import { Card } from "@/components/Kpi";
import { eurShort, num } from "@/lib/format";

const ANNO = 2026;

/** Mappa di copertura della provincia di competenza del commerciale: un cerchio per comune,
 *  grigio se scoperto, rosso se ha convenzionati (dimensione = popolazione, popup con CAP e dealer). */
export function MappaZona({ slug }) {
  const [dati, setDati] = useState(null);
  const [cerca, setCerca] = useState("");
  const [sel, setSel] = useState(null);
  const [ordine, setOrdine] = useState("abitanti");
  const box = useRef(null);
  const mappa = useRef(null);
  const layer = useRef(null);

  useEffect(() => {
    api.get(`/commerciali/${slug}/mappa-zona?anno=${ANNO}`).then((r) => setDati(r.data)).catch(() => setDati(null));
  }, [slug]);

  const comuni = dati?.comuni || [];
  const filtrati = useMemo(() => {
    const v = cerca.trim().toLowerCase();
    const lista = v ? comuni.filter((c) => c.comune.toLowerCase().includes(v) || c.cap.some((cap) => cap.startsWith(v))) : [...comuni];
    return lista.sort((a, b) => (ordine === "distanza"
      ? (a.distanza_km ?? 1e9) - (b.distanza_km ?? 1e9)
      : (b.popolazione || 0) - (a.popolazione || 0)));
  }, [comuni, cerca, ordine]);

  useEffect(() => {
    if (!comuni.length || !box.current) return undefined;
    if (!mappa.current) {
      mappa.current = L.map(box.current, { scrollWheelZoom: true });
      L.tileLayer("https://tile.openstreetmap.org/{z}/{x}/{y}.png", { attribution: "&copy; OpenStreetMap", maxZoom: 19 }).addTo(mappa.current);
      layer.current = L.layerGroup().addTo(mappa.current);
    }
    layer.current.clearLayers();
    const bounds = [];
    if (dati?.base) {
      L.circleMarker([dati.base.lat, dati.base.lng], { radius: 9, color: "#0F172A", weight: 3, fillColor: "#FACC15", fillOpacity: 1 })
        .bindTooltip(`<b>${dati.base.comune}</b> · base`, { permanent: true, direction: "top" }).addTo(layer.current);
      bounds.push([dati.base.lat, dati.base.lng]);
    }
    comuni.forEach((c) => {
      const coperto = c.convenzionati.length > 0;
      const r = Math.max(7, Math.min(16, Math.sqrt(c.popolazione || 1000) / 12));
      const m = L.circleMarker([c.lat, c.lng], {
        radius: coperto ? r + 2 : r, color: coperto ? "#C8102E" : "#64748B", weight: 2,
        fillColor: coperto ? "#C8102E" : "#CBD5E1", fillOpacity: coperto ? 0.85 : 0.7,
      });
      m.bindTooltip(`<b>${c.comune}</b> · ${c.cap.join(", ")}${c.popolazione ? ` · ${num(c.popolazione)} abitanti` : ""}${c.distanza_km != null ? ` · ${c.distanza_km} km` : ""}<br/>${coperto ? `${c.convenzionati.length} convenzionati · ${num(c.pratiche)} pratiche · ${eurShort(c.importo)}` : "SCOPERTO · nessun convenzionato"}`);
      m.on("click", () => setSel(c));
      m.addTo(layer.current);
      bounds.push([c.lat, c.lng]);
    });
    const adatta = () => { mappa.current?.invalidateSize(); mappa.current?.fitBounds(bounds, { padding: [12, 12] }); };
    setTimeout(adatta, 150);
    setTimeout(adatta, 900);
    return undefined;
  }, [comuni]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => () => { mappa.current?.remove(); mappa.current = null; }, []);

  if (!dati || !dati.zona?.length) return null;
  const t = dati.totali;
  return (
    <Card className="p-0 overflow-hidden" data-testid="mappa-zona">
      <div className="px-4 py-3 border-b border-slate-100 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="uppercase text-xs tracking-[0.18em] font-semibold text-slate-500">Zona di competenza · provincia di {dati.zona.join(", ")}</h3>
          <div className="text-xs text-slate-400 mt-0.5" data-testid="mappa-zona-totali">
            {num(t.comuni)} comuni · {num(t.cap)} CAP · {num(t.abitanti || 0)} abitanti · <span className="text-[#C8102E] font-semibold">{num(t.coperti)} coperti</span> · {num(t.scoperti)} scoperti
            {t.convenzionati ? ` · ${num(t.convenzionati)} convenzionati · ${num(t.pratiche)} pratiche · ${eurShort(t.importo)}` : ""}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="inline-flex rounded-md border border-slate-200 overflow-hidden text-xs" data-testid="mappa-zona-ordine">
            {[["abitanti", "Abitanti"], ["distanza", `Distanza da ${dati.base?.comune || "base"}`]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => setOrdine(v)} data-testid={`mappa-zona-ordine-${v}`}
                className={`px-3 py-1.5 transition-colors duration-200 ${ordine === v ? "bg-slate-900 text-white" : "bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
          <input value={cerca} onChange={(e) => setCerca(e.target.value)} placeholder="Cerca comune o CAP…" data-testid="mappa-zona-cerca"
            className="border border-slate-200 rounded-md px-3 py-1.5 text-sm bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-sky-300 w-48" />
        </div>
      </div>
      <div className="grid lg:grid-cols-[2fr_1fr]">
        <div className="relative">
          <div ref={box} className="h-[640px] w-full" data-testid="mappa-zona-mappa" />
          <div className="absolute bottom-3 left-3 z-[500] bg-white/90 rounded-md shadow px-3 py-2 text-[11px] flex gap-4" data-testid="mappa-zona-legenda">
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#C8102E]" /> con convenzionati</span>
            <span className="inline-flex items-center gap-1.5"><span className="w-3 h-3 rounded-full bg-[#CBD5E1] border-2 border-[#64748B]" /> scoperto</span>
          </div>
        </div>
        <div className="border-l border-slate-100 h-[640px] overflow-y-auto text-sm">
          {sel && (
            <div className="p-4 border-b border-slate-100 bg-slate-50/70" data-testid="mappa-zona-selezione">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="font-semibold text-slate-900">{sel.comune}</div>
                  <div className="text-xs text-slate-500 font-mono">{sel.cap.join(" · ")}</div>
                  {sel.popolazione ? <div className="text-xs text-slate-500">{num(sel.popolazione)} abitanti{sel.distanza_km != null ? ` · ${sel.distanza_km.toLocaleString("it-IT", { maximumFractionDigits: 1 })} km da ${dati.base?.comune}` : ""}</div> : null}
                </div>
                <button type="button" onClick={() => setSel(null)} className="text-xs text-slate-500 hover:text-slate-900" data-testid="mappa-zona-chiudi">✕</button>
              </div>
              {sel.convenzionati.length ? sel.convenzionati.map((d) => (
                <div key={d.codice_dealer} className="mt-2 text-xs flex justify-between gap-2">
                  <span className="text-slate-700">{d.ragione_sociale} <span className="text-slate-400 font-mono">{d.codice_dealer}</span></span>
                  <span className="font-mono">{num(d.pratiche)} · {eurShort(d.importo)}</span>
                </div>
              )) : <div className="mt-2 text-xs text-slate-500">Nessun convenzionato: comune scoperto.</div>}
            </div>
          )}
          <table className="w-full text-xs" data-testid="mappa-zona-tabella">
            <thead className="sticky top-0 bg-white text-[10px] uppercase tracking-wider text-slate-500">
              <tr><th className="text-left px-3 py-2">Comune</th><th className="text-left px-3 py-2">CAP</th><th className="text-right px-3 py-2">Abitanti</th><th className="text-right px-3 py-2">km</th><th className="text-right px-3 py-2">Conv.</th><th className="text-right px-3 py-2">Caricato</th></tr>
            </thead>
            <tbody>
              {filtrati.map((c) => (
                <tr key={c.comune} onClick={() => { setSel(c); mappa.current?.setView([c.lat, c.lng], 12); }}
                  className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors duration-150 ${sel?.comune === c.comune ? "bg-sky-50" : ""}`}
                  data-testid={`mappa-zona-comune-${c.comune.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
                  <td className="px-3 py-1.5">
                    <span className={`inline-block w-2 h-2 rounded-full mr-2 ${c.convenzionati.length ? "bg-[#C8102E]" : "bg-slate-300"}`} />{c.comune}
                  </td>
                  <td className="px-3 py-1.5 font-mono text-slate-500">{c.cap.length > 2 ? `${c.cap[0]}–${c.cap[c.cap.length - 1]}` : c.cap.join(", ")}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-slate-600">{c.popolazione ? num(c.popolazione) : "–"}</td>
                  <td className="px-3 py-1.5 text-right font-mono text-slate-600">{c.distanza_km != null ? c.distanza_km.toLocaleString("it-IT", { maximumFractionDigits: 0 }) : "–"}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{c.convenzionati.length || "–"}</td>
                  <td className="px-3 py-1.5 text-right font-mono">{c.importo ? eurShort(c.importo) : "–"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Card>
  );
}
